import { normalizeCurrencyValue } from '@/lib/utils'
import type { PosCartCustomer } from '@/lib/services/carts/carts.types'

/**
 * What Pay Later would leave on the customer's account and whether that passes their credit limit, as the server
 * projects it (`CustomerBillingEligibilityService::projection`, money rule 4): the cart's payment session
 * (`pay_later`), or an order change's quote summary.
 */
export interface PayLaterProjection {
  projected_open_balance_total: string | null
  over_credit_limit: boolean
}

export type CustomerReceivablesTone = 'neutral' | 'warning' | 'danger'

type ReceivablesCustomer = Partial<
  Pick<PosCartCustomer, 'pay_later_allowed' | 'credit_limit' | 'summary'>
>

export function getCustomerReceivablesTone({
  openBalance,
  overdueBalance,
  creditLimit,
}: {
  openBalance: number
  overdueBalance: number
  creditLimit: number | null
}): CustomerReceivablesTone {
  if (openBalance <= 0) {
    return 'neutral'
  }

  if (creditLimit !== null && openBalance > creditLimit) {
    return 'danger'
  }

  if (overdueBalance > 0) {
    return 'warning'
  }

  return 'neutral'
}

export function getCustomerReceivablesContext({
  customer,
  projection = null,
}: { customer?: ReceivablesCustomer | null; projection?: PayLaterProjection | null } = {}) {
  const payLaterAllowed = customer?.pay_later_allowed !== false
  const openBalance = normalizeCurrencyValue(customer?.summary?.open_balance_total ?? 0)
  const overdueBalance = normalizeCurrencyValue(customer?.summary?.overdue_balance_total ?? 0)
  const creditLimit =
    payLaterAllowed && customer?.credit_limit != null
      ? normalizeCurrencyValue(customer.credit_limit)
      : null

  return {
    payLaterAllowed,
    openBalance,
    overdueBalance,
    creditLimit,
    projectedOpenBalance: projection?.projected_open_balance_total ?? null,
    isProjectedOverCreditLimit: projection?.over_credit_limit === true,
    balanceTone: getCustomerReceivablesTone({
      openBalance,
      overdueBalance,
      creditLimit,
    }),
  }
}
